const BASE = '/api/v1';
const CSRF_COOKIE_NAME = 'nev_admin_csrf';

export interface ApiProblem {
  readonly status: number;
  readonly code: string;
  readonly title: string;
}

export type ApiResult<T> =
  { readonly ok: true; readonly value: T } | { readonly ok: false; readonly problem: ApiProblem };

export interface AdminDocument {
  readonly id: string;
  readonly kind: 'participant-initial' | 'participant-final' | 'admin-screening';
  readonly state:
    | 'not_requested'
    | 'queued'
    | 'processing'
    | 'available'
    | 'retry_wait'
    | 'failed'
    | 'superseded'
    | 'invalidated'
    | 'deleted';
  readonly created_at: string;
  readonly logical_hash?: string;
}

function readCsrfCookie(): string | undefined {
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${CSRF_COOKIE_NAME}=`));
  return match?.slice(CSRF_COOKIE_NAME.length + 1);
}

async function problem(response: Response): Promise<ApiProblem> {
  const body = (await response.json().catch(() => null)) as {
    code?: string;
    title?: string;
  } | null;
  return {
    status: response.status,
    code: body?.code ?? 'UNKNOWN',
    title: body?.title ?? 'Não foi possível concluir a operação.',
  };
}

function filename(response: Response): string {
  const disposition = response.headers.get('Content-Disposition') ?? '';
  return /filename="([^"]+)"/i.exec(disposition)?.[1] ?? 'documento-nev.pdf';
}

export const documentosApi = {
  async list(
    participationId: string,
  ): Promise<ApiResult<{ items: readonly AdminDocument[]; has_more: boolean }>> {
    let response: Response;
    try {
      response = await fetch(
        `${BASE}/admin/participations/${encodeURIComponent(participationId)}/documents`,
        { method: 'GET', headers: { Accept: 'application/json' }, credentials: 'same-origin' },
      );
    } catch {
      return {
        ok: false,
        problem: {
          status: 0,
          code: 'NETWORK_ERROR',
          title: 'Não foi possível conectar ao serviço.',
        },
      };
    }
    if (!response.ok) return { ok: false, problem: await problem(response) };
    return {
      ok: true,
      value: (await response.json()) as { items: readonly AdminDocument[]; has_more: boolean },
    };
  },

  async download(input: {
    documentId: string;
    reauthToken: string;
    idempotencyKey: string;
  }): Promise<ApiResult<{ blob: Blob; filename: string }>> {
    const headers = new Headers({
      Accept: 'application/json',
      'Idempotency-Key': input.idempotencyKey,
      'X-Reauthentication-Token': input.reauthToken,
    });
    const csrf = readCsrfCookie();
    if (csrf !== undefined) headers.set('X-CSRF-Token', csrf);
    let issued: Response;
    try {
      issued = await fetch(`${BASE}/documents/${encodeURIComponent(input.documentId)}/download`, {
        method: 'POST',
        headers,
        credentials: 'same-origin',
      });
    } catch {
      return {
        ok: false,
        problem: {
          status: 0,
          code: 'NETWORK_ERROR',
          title: 'Não foi possível conectar ao serviço.',
        },
      };
    }
    if (!issued.ok) return { ok: false, problem: await problem(issued) };
    const ticket = (await issued.json()) as { download_path?: unknown };
    if (typeof ticket.download_path !== 'string' || !ticket.download_path.startsWith('/api/v1/')) {
      return {
        ok: false,
        problem: {
          status: 502,
          code: 'INVALID_API_RESPONSE',
          title: 'O serviço devolveu uma resposta inesperada.',
        },
      };
    }
    let downloaded: Response;
    try {
      downloaded = await fetch(ticket.download_path, {
        method: 'GET',
        headers: { Accept: 'application/pdf' },
        credentials: 'same-origin',
      });
    } catch {
      return {
        ok: false,
        problem: {
          status: 0,
          code: 'NETWORK_ERROR',
          title: 'Não foi possível conectar ao serviço.',
        },
      };
    }
    if (!downloaded.ok) return { ok: false, problem: await problem(downloaded) };
    return {
      ok: true,
      value: { blob: await downloaded.blob(), filename: filename(downloaded) },
    };
  },
};
